import { z } from 'zod';
import { create } from 'zustand';

import { initDolyamiPaymentAction } from '@/features/payment/actions/init-dolyami-payment-action';
import { initFullPaymentAction } from '@/features/payment/actions/init-full-payment';
import {
	PAYMENT_DURATIONS,
	PAYMENT_METHODS,
	PRODUCT_TYPES,
} from '@/features/payment/const';

import { initLoanAction, validatePromoCodeAction } from '../actions';
import { PaymentFormValues } from '../types';

interface FormErrors {
	startDate?: string[];
	paymentMethod?: string[];
	isAgree?: string[];
	paymentDuration?: string[];
	userEmail?: string[];
	promoCode?: string[];
}

const paymentSchema = z.object({
	productId: z.string(),
	tariffId: z.string(),
	startDate: z.string(),
	productType: z.enum([PRODUCT_TYPES.TARIFF, PRODUCT_TYPES.SUBSCRIPTION]),
	paymentMethod: z.enum([
		PAYMENT_METHODS.LOAN,
		PAYMENT_METHODS.FULL,
		PAYMENT_METHODS.DOLYAMI,
	]),
	paymentDuration: z.enum([
		PAYMENT_DURATIONS['3'],
		PAYMENT_DURATIONS['4'],
		PAYMENT_DURATIONS['6'],
	]),
	isAgree: z.boolean().refine((val) => val, {
		message: 'Необходимо согласие с офертой',
	}),
	userEmail: z.string().email('Некорректный e-mail').optional(),
	advertisingMail: z.boolean().optional(),
});

const steps = ['Ваша почта для доступа', 'Способы оплаты', 'Оплата'];

interface PaymentStore {
	steps: string[];
	activeStep: number;
	activeStepName: string;
	values: PaymentFormValues;
	errors: FormErrors;
	isPending: boolean;
	isAuthorized: boolean;
	isPromoCodeValidating: boolean;
	setActiveStep: (step: number) => void;
	updateValues: (values: Partial<PaymentFormValues>) => void;
	setErrors: (errors: FormErrors) => void;
	resetErrors: () => void;

	initialize: ({
		productId,
		tariffId,
		startDate,
		productType,
		paymentMethod,
		isAuthorized,
		promoCode,
	}: {
		productId: string;
		tariffId: string;
		startDate: string;
		productType: PaymentFormValues['productType'];
		paymentMethod?: PaymentFormValues['paymentMethod'];
		isAuthorized?: boolean;
		promoCode?: string;
	}) => void;

	validatePromoCode: () => Promise<{
		success: boolean;
		error: string;
	}>;

	validateForm: () => boolean;

	submitForm: () => Promise<{
		success: boolean;
		error: string;
	}>;

	goNextStep: () => void;
	goPrevStep: () => void;
}

const initState = {
	activeStep: 0,
	activeStepName: steps[0],
	steps,
	values: {
		productId: '',
		tariffId: '',
		targetId: '',
		startDate: '',
		productType: PRODUCT_TYPES.TARIFF,
		paymentMethod: PAYMENT_METHODS.DOLYAMI,
		paymentDuration: PAYMENT_DURATIONS['3'],
		isAgree: true,
		userEmail: '',
		advertisingMail: true,
		promoCode: '',
		promoCodeValidated: false,
		promoCodeType: null,
	},
	errors: {},
	isPending: false,
	isAuthorized: false,
	isPromoCodeValidating: false,
};

export const usePaymentStore = create<PaymentStore>((set, get) => {
	return {
		...initState,
		setActiveStep: (step) =>
			set({
				activeStep: step,
				activeStepName: steps[step],
			}),
		goNextStep: () => {
			const currentStep = get().activeStep;
			const state = get();

			// Валидация для первого шага (ввод email) только для неавторизованных пользователей
			if (currentStep === 0 && !state.isAuthorized) {
				const emailSchema = z.string().email('Некорректный e-mail');
				const result = emailSchema.safeParse(state.values.userEmail);

				if (!result.success) {
					set({
						errors: {
							...state.errors,
							userEmail: [result.error.errors[0].message],
						},
					});
					return;
				}
				// Сбрасываем ошибки email если валидация прошла
				const newErrors = { ...state.errors };
				delete newErrors.userEmail;
				set({ errors: newErrors });
			}

			if (currentStep < get().steps.length - 1) {
				set({
					activeStep: currentStep + 1,
					activeStepName: get().steps[currentStep + 1],
				});
			}
		},
		goPrevStep: () => {
			if (get().activeStep > 0) {
				set({
					activeStep: get().activeStep - 1,
					activeStepName: steps[get().activeStep - 1],
				});
			}
		},

		updateValues: (newValues) =>
			set((state) => ({
				values: { ...state.values, ...newValues },
				// Сбрасываем ошибки для обновленных полей
				errors: Object.keys(newValues).reduce(
					(errors, key) => {
						const newErrors = { ...errors };
						delete newErrors[key as keyof FormErrors];
						return newErrors;
					},
					{ ...state.errors },
				),
			})),

		setErrors: (errors) => set({ errors }),

		resetErrors: () => set({ errors: {} }),

		initialize: ({
			productId,
			startDate,
			tariffId,
			productType,
			paymentMethod,
			isAuthorized = false,
			promoCode = '',
		}) => {
			// Определяем доступные шаги в зависимости от авторизации
			const availableSteps = isAuthorized
				? ['Способы оплаты', 'Оплата']
				: steps;

			set({
				values: {
					...initState.values,
					productId,
					startDate,
					tariffId,
					productType: productType || initState.values.productType,
					paymentMethod: paymentMethod || initState.values.paymentMethod,
					promoCode: promoCode || '',
					promoCodeValidated: false,
					promoCodeType: null,
				},
				errors: {},
				activeStep: 0,
				activeStepName: availableSteps[0],
				steps: availableSteps,
				isAuthorized,
			});
		},

		validatePromoCode: async () => {
			const state = get();
			const promoCode = state.values.promoCode.trim();

			if (!promoCode) {
				set({
					errors: {
						...state.errors,
						promoCode: ['Введите промокод'],
					},
				});
				return {
					success: false,
					error: 'Введите промокод',
				};
			}

			set({ isPromoCodeValidating: true });

			// Добавляем искусственную задержку минимум 500мс
			const startTime = Date.now();
			const { success, error, promoCodeType } =
				await validatePromoCodeAction(promoCode);
			const elapsed = Date.now() - startTime;
			const delay = Math.max(0, 500 - elapsed);

			if (delay > 0) {
				await new Promise((resolve) => setTimeout(resolve, delay));
			}

			set({ isPromoCodeValidating: false });

			if (!success) {
				set({
					errors: {
						...state.errors,
						promoCode: [error],
					},
					values: {
						...state.values,
						promoCodeValidated: false,
						promoCodeType: null,
					},
				});
				return {
					success: false,
					error,
				};
			}

			// Сбрасываем ошибки промокода при успешной валидации
			const newErrors = { ...state.errors };
			delete newErrors.promoCode;

			set({
				errors: newErrors,
				values: {
					...state.values,
					promoCodeValidated: true,
					promoCodeType,
				},
			});

			return {
				success: true,
				error: '',
			};
		},

		validateForm: () => {
			const state = get();

			// Создаем схему валидации в зависимости от авторизации
			const currentSchema = state.isAuthorized
				? paymentSchema.omit({ userEmail: true }) // Исключаем email для авторизованных
				: paymentSchema;

			const result = currentSchema.safeParse(state.values);

			if (!result.success) {
				const formattedErrors = result.error.format();
				const fullSchemaErrors = !state.isAuthorized
					? paymentSchema.safeParse(state.values)
					: null;
				const newErrors: FormErrors = {};

				if (formattedErrors.startDate?._errors) {
					newErrors.startDate = formattedErrors.startDate._errors;
				}

				if (formattedErrors.paymentMethod?._errors) {
					newErrors.paymentMethod = formattedErrors.paymentMethod._errors;
				}

				if (formattedErrors.isAgree?._errors) {
					newErrors.isAgree = formattedErrors.isAgree._errors;
				}

				// Только для неавторизованных пользователей проверяем email
				if (
					!state.isAuthorized &&
					fullSchemaErrors &&
					!fullSchemaErrors.success
				) {
					const fullErrors = fullSchemaErrors.error.format();
					if (fullErrors.userEmail?._errors) {
						newErrors.userEmail = fullErrors.userEmail._errors;
					}
				}

				set({ errors: newErrors });
				return false;
			}

			set({ errors: {} });
			return true;
		},

		submitForm: async () => {
			const isValid = get().validateForm();
			if (!isValid) {
				return {
					success: false,
					error: Object.values(get().errors).join(', '),
				};
			}

			if (get().values.paymentMethod === PAYMENT_METHODS.LOAN) {
				set({ isPending: true });
				const loanPaymentData = {
					productType: get().values.productType,
					productId: get().values.productId,
					...(get().isAuthorized
						? {}
						: {
								userEmail: get().values.userEmail,
								isAnonymous: true,
								advertisingMail: get().values.advertisingMail,
							}),
					providerInput: {
						duration: get().values.paymentDuration,
					},
				};
				const { success, redirectUrl, error } =
					await initLoanAction(loanPaymentData);
				set({ isPending: false });
				if (success) {
					window.location.href = redirectUrl;
					return {
						success: true,
						error: '',
					};
				}
				return {
					success: false,
					error,
				};
			}

			if (get().values.paymentMethod === PAYMENT_METHODS.FULL) {
				set({ isPending: true });
				const fullPaymentData = {
					productType: get().values.productType,
					productId: get().values.productId,
					...(get().isAuthorized
						? {}
						: {
								userEmail: get().values.userEmail,
								isAnonymous: true,
								advertisingMail: get().values.advertisingMail,
							}),
					providerInput: {
						recurrent: get().values.productType === PRODUCT_TYPES.SUBSCRIPTION,
					},
					// Добавляем промокод только если он валидирован
					...(get().values.promoCodeValidated && get().values.promoCode
						? { promoCode: get().values.promoCode.trim() }
						: {}),
				};
				const { success, redirectUrl, error } =
					await initFullPaymentAction(fullPaymentData);
				set({ isPending: false });
				if (success) {
					window.location.href = redirectUrl;
					return {
						success: true,
						error: '',
					};
				}
				return {
					success: false,
					error,
				};
			}

			if (get().values.paymentMethod === PAYMENT_METHODS.DOLYAMI) {
				set({ isPending: true });
				const dolyamiData = {
					productType: get().values.productType,
					productId: get().values.productId,
					providerInput: {},
					...(get().isAuthorized
						? {}
						: {
								userEmail: get().values.userEmail,
								isAnonymous: true,
								advertisingMail: get().values.advertisingMail,
							}),
				};
				const { success, redirectUrl, error } =
					await initDolyamiPaymentAction(dolyamiData);
				set({ isPending: false });
				if (success) {
					window.location.href = redirectUrl;
					return {
						success: true,
						error: '',
					};
				}
				return {
					success: false,
					error,
				};
			}

			return {
				success: false,
				error: 'Такой способ оплаты не поддерживается',
			};
		},
	};
});
